/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Table, DatePicker, Spin, message, Button, Tooltip, Select, Input } from 'antd';
import moment from 'moment';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styles from './styles';
import {
  getCustomerKpis,
  getCustomerLicenses,
  getDeliveriesByDay,
  getRecentOrders,
  getTopLocations,
  getOrdersInRange,
  getCustomerReturns,
} from '../../_shared/services/customer-dashboard.service';
import { getStatusIcon } from '../../models/order';
import { OrderModal } from '../orders/orders.component';
import { getUserData } from '../../authentication/authentication.actions';
import { exportToCsv, getConfig, getManifestUrl, getRawLicense, getReconUrl, loadGPSID, orderHasManifest, searchTable } from '../../actions/utils';
import {
  WEEKDAY_LABELS,
  dowKey,
  loadDays,
  saveDays,
  fmtDate,
  fmtTick,
  truncate,
  computeWeekdayAverages,
  getRejectionReason,
} from '../../_shared/dashboard-helpers';

const DEFAULT_RANGE = () => [
  moment().subtract(3, 'weeks').startOf('isoWeek'),
  moment().endOf('isoWeek'),
];

// Human-readable labels for order type enum values used in the Export Orders
// report (mirrors the option titles defined in order.component.jsx). Kept local
// to this file so the Admin/Reports exports are unaffected.
const ORDER_TYPE_LABELS = {
  layover: 'Pickup with Layover',
  dropoff: 'Dropoff with Layover',
  sameDay: 'Same Day',
  cash_return: 'Reconciliation Return',
  rej_return: 'Rejection Return',
  cash_pickup: 'Payment Pickup',
  other_return: 'Other',
};

// Non-delivery order types excluded from the Orders Export Report — these are
// return/cash orders, not outbound deliveries (Danielle 2026-06-16):
// Reconciliation Return, Rejection Return, Payment Pickup, Other.
const EXCLUDED_EXPORT_TYPES = ['cash_return', 'rej_return', 'cash_pickup', 'other_return'];

// Tile order — Danielle 2026-06-16: the top boxes follow the order lifecycle
// left-to-right: Total Deliveries (all deliveries for the period) → Upcoming
// (Approved) → Picked Up/Dropped Off → Out For Delivery (In Transit) →
// Delivered → Rejected.
const KPI_DEFS = [
  { key: 'total', title: 'Total Deliveries' },
  { key: 'upcoming', title: 'Upcoming Orders' },
  { key: 'pickedUp', title: 'Orders Picked Up/Dropped Off' },
  { key: 'outForDelivery', title: 'Orders Out for Delivery' },
  { key: 'delivered', title: 'Orders Delivered' },
  { key: 'rejected', title: 'Orders Rejected' },
];

const REJECTION_TEXT_LIMIT = 30;
const CHART_PX_PER_DAY = 70;
const CHART_VISIBLE_DAYS = 7;
const TOP_LOCATIONS_LIMIT = 15;

export const CustomerDashboard = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = get(user, 'id');
  const customerLogo =
    get(user, 'company.logo') ||
    get(user, 'data.company.logo') ||
    get(user, 'logo') ||
    '';
  // Company name shown in the Shipping Dashboard header. One rule for ALL
  // roles: show the Account page's "Company Name (LLC)" — company.Licensee —
  // not the stale signup-era company.DBA and not the license-map combined
  // "Licensee - DBA" string (Joel 2026-07-14, card #11 round 16). data.company
  // is read before the top-level company snapshot, which can be stale in old
  // redux-persist sessions. Fallbacks so the header is never blank: Super
  // Admin (0) / Admin (1) → this instance's own license Licensee (raw row, so
  // no " - DBA" suffix), then the stored DBA; customers → the stored DBA.
  const storedLLC =
    get(user, 'data.company.Licensee') ||
    get(user, 'company.Licensee') ||
    '';
  const storedDBA =
    get(user, 'data.company.DBA') ||
    get(user, 'company.DBA') ||
    '';
  const role = get(user, 'role');
  const isAdminRole = role === 0 || role === 1;
  const customerDBA =
    storedLLC ||
    (isAdminRole ? get(getRawLicense(loadGPSID()), 'Licensee', '') : storedDBA) ||
    (isAdminRole ? storedDBA : '');

  useEffect(() => {
    dispatch(getUserData());
  }, [dispatch]);

  const [range, setRange] = useState(DEFAULT_RANGE());
  const [loading, setLoading] = useState(false);
  const [recentLoading, setRecentLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [counts, setCounts] = useState({});
  const [series, setSeries] = useState([]);
  const [topLocations, setTopLocations] = useState([]);
  const [recent, setRecent] = useState({ completed: [], rejected: [] });
  const [returns, setReturns] = useState({ cashReturns: [], rejectionReturns: [] });
  const [returnsLoading, setReturnsLoading] = useState(false);
  const [editModal, setEditModal] = useState(undefined);
  const [allLicenses, setAllLicenses] = useState([]);
  // Card #277 — one term, applied to every table on this page. These columns are all
  // `{ title, key, render }` with no dataIndex, so the match is made against what each
  // cell drew; each table holds a handful of rows, which is what makes that affordable.
  const [search, setSearch] = useState('');
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  // Day-of-week selections persist per-user via localStorage (Danielle
  // 2026-06-03 — so Sat/Sun stay deselected across sessions). Keys are
  // namespaced by userId + dashboard + chart.
  const lineDaysKey = dowKey(userId, 'shipping', 'line');
  const avgDaysKey = dowKey(userId, 'shipping', 'avg');
  const [lineDays, setLineDaysState] = useState(() => loadDays(lineDaysKey));
  const [avgDays, setAvgDaysState] = useState(() => loadDays(avgDaysKey));
  const setLineDays = days => {
    setLineDaysState(days);
    saveDays(lineDaysKey, days);
  };
  const setAvgDays = days => {
    setAvgDaysState(days);
    saveDays(avgDaysKey, days);
  };
  // userId may be absent on the very first render (before persisted/refreshed
  // user data is available); re-sync the saved selections once it resolves.
  useEffect(() => {
    setLineDaysState(loadDays(lineDaysKey));
    setAvgDaysState(loadDays(avgDaysKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // All / Internal / External order filter. Unlike the Admin Dashboard's
  // dropdown (gated on Internal Distribution), this one is gated purely by the
  // dashboard.shipping.showOrderDefFilter setting and shown to EVERYONE who can
  // view the Shipping Dashboard — no role gate (Danielle 2026-07-01). Reuses the
  // existing per-order data.order.orderDef ('int'/'ext') tagging. 'all' => no
  // server-side orderDef filter (undefined).
  const showOrderDefFilter = getConfig('dashboard.shipping.showOrderDefFilter', false);
  const [orderDef, setOrderDef] = useState('all');
  const orderDefParam = orderDef === 'all' ? undefined : orderDef;

  // Per-section visibility (Edit Settings → Dashboard → Shipping Dashboard
  // Hidden Sections). Empty list = show everything.
  const shippingHiddenSections = getConfig('dashboard.shipping.hiddenSections', []) || [];
  // Master Rejection Returns toggle (Edit Settings → Orders → Enable Rejection
  // Returns). Default on; when off, the Rejection Returns Scheduled section is
  // hidden regardless of the hidden-sections list.
  const rejectionReturnsEnabled = getConfig('orders.rejectionReturns.enabled', true);
  const showShip = key => !shippingHiddenSections.includes(key)
    && !(key === 'rejectionReturns' && !rejectionReturnsEnabled);

  const licenseFilter = useMemo(() => {
    if (!allLicenses.length) return undefined;
    if (!selectedLicenses.length || selectedLicenses.length === allLicenses.length) return undefined;
    return selectedLicenses;
  }, [allLicenses, selectedLicenses]);

  const reversedSeries = useMemo(() => [...(series || [])].reverse(), [series]);
  const weekdayAverages = useMemo(() => computeWeekdayAverages(series), [series]);

  const lineSeriesFiltered = useMemo(() => {
    if (!lineDays.length || lineDays.length === WEEKDAY_LABELS.length) return reversedSeries;
    return reversedSeries.filter(row => {
      const idx = moment(row.date).isoWeekday() - 1;
      return idx >= 0 && idx <= 6 && lineDays.includes(WEEKDAY_LABELS[idx]);
    });
  }, [reversedSeries, lineDays]);

  const avgFiltered = useMemo(() => {
    if (!avgDays.length || avgDays.length === WEEKDAY_LABELS.length) return weekdayAverages;
    return weekdayAverages.filter(b => avgDays.includes(b.day));
  }, [weekdayAverages, avgDays]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const lns = await getCustomerLicenses({ userId });
        if (cancelled) return;
        setAllLicenses(lns);
        setSelectedLicenses(lns);
      } catch (err) {
        if (!cancelled) message.error(`Failed to load licenses: ${err.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const loadRangeData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [k, s, l] = await Promise.all([
        getCustomerKpis({ userId, range, licenses: licenseFilter, orderDef: orderDefParam }),
        getDeliveriesByDay({ userId, range, licenses: licenseFilter, orderDef: orderDefParam }),
        getTopLocations({ userId, range, limit: TOP_LOCATIONS_LIMIT, licenses: licenseFilter, orderDef: orderDefParam }),
      ]);
      setCounts(k);
      setSeries(s);
      setTopLocations(l);
    } catch (err) {
      message.error(`Failed to load dashboard: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, range, licenseFilter, orderDefParam]);

  const loadRecent = useCallback(async () => {
    if (!userId) return;
    setRecentLoading(true);
    try {
      const r = await getRecentOrders({ userId, completedLimit: 10, rejectedLimit: 5, licenses: licenseFilter, orderDef: orderDefParam });
      setRecent(r);
    } catch (err) {
      message.error(`Failed to load recent orders: ${err.message}`);
    } finally {
      setRecentLoading(false);
    }
  }, [userId, licenseFilter, orderDefParam]);

  const loadReturns = useCallback(async () => {
    if (!userId) return;
    setReturnsLoading(true);
    try {
      const r = await getCustomerReturns({ userId, range, licenses: licenseFilter, orderDef: orderDefParam });
      setReturns(r);
    } catch (err) {
      message.error(`Failed to load returns: ${err.message}`);
    } finally {
      setReturnsLoading(false);
    }
  }, [userId, range, licenseFilter, orderDefParam]);

  useEffect(() => { loadRangeData(); }, [loadRangeData]);
  useEffect(() => { loadRecent(); }, [loadRecent]);
  useEffect(() => { loadReturns(); }, [loadReturns]);

  const refreshAll = () => {
    loadRangeData();
    loadRecent();
    loadReturns();
  };

  const openOrder = record => setEditModal({ state: { editOrder: record } });
  const closeOrder = () => setEditModal(undefined);

  const renderManifest = (_, row) => {
    const num = get(row, 'data.billing.manifest', '') || '';
    if (!num) return '';
    const { file } = orderHasManifest(row);
    const url = getManifestUrl(file);
    if (!url) return num;
    return (
      <a css={css(styles.orderLink)} href={url} target="_blank" rel="noopener noreferrer">{num}</a>
    );
  };

  const baseColumns = [
    {
      title: 'Order ID',
      key: 'orderId',
      render: (_, row) => (
        <a css={css(styles.orderLink)} onClick={() => openOrder(row)}>{row.orderId}</a>
      ),
    },
    { title: 'Customer Invoice', key: 'invoice', render: (_, row) => get(row, 'data.billing.invoice', '') || '' },
    { title: 'Delivery Date', key: 'deliveryDate', render: (_, row) => fmtDate(get(row, 'data.order.deliveryDate') || row.delivery_date || row.deliveryDate) },
    { title: 'Dest. Name', key: 'destName', render: (_, row) => get(row, 'data.destCompany.Licensee', '') || '' },
    { title: 'DBA', key: 'dba', render: (_, row) => get(row, 'data.destCompany.DBA', '') || '' },
    { title: 'Manifest', key: 'manifest', render: renderManifest },
    { title: 'Status', key: 'status', render: (_, row) => get(getStatusIcon(row, { isAdmin: false }), 'title', '') },
  ];

  const rejectionColumns = [
    ...baseColumns,
    {
      title: 'Rejection Reason',
      key: 'rejectionReason',
      render: (_, row) => {
        const full = getRejectionReason(row);
        if (!full) return '';
        return (
          <Tooltip title={full} placement="topLeft">
            <span>{truncate(full, REJECTION_TEXT_LIMIT)}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Reconciliation Returns / Rejection Returns containers — mirror the Admin
  // Dashboard. "Customer Pickup" flagged returns show CUSTOMER PICKUP instead
  // of the return date; the Reconciliation container adds a Recon ID quick
  // link to the signed reconciliation PDF (Danielle 2026-05-19).
  const returnDateColumn = {
    title: 'Return Date',
    key: 'returnDate',
    render: (_, row) => get(row, 'data.order.customerPickup')
      ? 'CUSTOMER PICKUP'
      : fmtDate(get(row, 'data.order.deliveryDate') || row.delivery_date || row.deliveryDate),
  };
  const returnOrderIdColumn = {
    title: 'Order ID',
    key: 'orderId',
    render: (_, row) => (
      <a css={css(styles.orderLink)} onClick={() => openOrder(row)}>{row.orderId}</a>
    ),
  };
  // Shipping Dashboard / Reconciliation Returns Scheduled container: Order ID
  // displays as plain text (no hyperlink) per Danielle 2026-05-20.
  const reconReturnOrderIdColumn = {
    title: 'Order ID',
    key: 'orderId',
    render: (_, row) => row.orderId || '',
  };
  // Recon ID string removed per Danielle 2026-05-20 — keep only the Link button
  // to the signed reconciliation PDF.
  const reconIdColumn = {
    title: 'Recon ID',
    key: 'reconId',
    render: (_, row) => {
      const url = getReconUrl(get(row, 'data.recon.file'));
      if (!url) return '';
      return (
        <a css={css(styles.orderLink)} href={url} target="_blank" rel="noopener noreferrer">Link</a>
      );
    },
  };
  const returnPickupCol = { title: 'Pickup', key: 'pickup', render: (_, row) => get(row, 'data.pickupCompany.Licensee', '') || '' };
  const returnDestCol = { title: 'Destination', key: 'destName', render: (_, row) => get(row, 'data.destCompany.Licensee', '') || '' };
  const returnDbaCol = { title: 'DBA', key: 'dba', render: (_, row) => get(row, 'data.destCompany.DBA', '') || '' };
  const returnStatusCol = { title: 'Status', key: 'status', render: (_, row) => get(getStatusIcon(row, { isAdmin: false }), 'title', '') };
  const reconReturnColumns = [returnDateColumn, reconReturnOrderIdColumn, reconIdColumn, returnPickupCol, returnDestCol, returnDbaCol, returnStatusCol];
  const rejectionReturnColumns = [returnDateColumn, returnOrderIdColumn, returnPickupCol, returnDestCol, returnDbaCol, returnStatusCol];

  const topLocationColumns = [
    { title: 'License #', dataIndex: 'licenseNumber', key: 'licenseNumber', className: 'license-col' },
    { title: 'Destination Name', dataIndex: 'destName', key: 'destName' },
    { title: 'DBA', dataIndex: 'dba', key: 'dba' },
    { title: '# of Deliveries', dataIndex: 'count', key: 'count' },
  ];

  const exportOrders = async () => {
    if (!userId) return;
    setExporting(true);
    try {
      // Export every order in the selected date range regardless of status
      // (Danielle 2026-05-19 — the list was only showing delivered/rejected,
      // so e.g. a 137-order timeframe exported only ~11 rows).
      const orders = await getOrdersInRange({
        userId,
        range,
        licenses: licenseFilter,
        orderDef: orderDefParam,
      });
      const rows = [
        [
          'Order ID',
          'Delivery Date',
          'Destination Name',
          'Destination DBA',
          'Customer Invoice',
          'Invoice Amount',
          'Payment Method',
          'Manifest Number',
          'Status',
          'Order Type',
          'Rejection Reason',
        ],
      ];
      orders.forEach(row => {
        // Exclude Cancelled orders (status 8 = OrderStatus.orderCancelled).
        // Deleted orders are already excluded server-side (deleted: 0).
        if (get(row, 'data.order.status') === 8) return;
        // Exclude non-delivery order types from the Orders Export Report:
        // Reconciliation Return, Rejection Return, Payment Pickup, and Other
        // are not outbound deliveries (Danielle 2026-06-16).
        if (EXCLUDED_EXPORT_TYPES.includes(get(row, 'data.order.orderType'))) return;
        rows.push([
          row.orderId || '',
          fmtDate(get(row, 'data.order.deliveryDate') || row.delivery_date || row.deliveryDate),
          get(row, 'data.destCompany.Licensee', '') || '',
          get(row, 'data.destCompany.DBA', '') || '',
          get(row, 'data.billing.invoice', '') || '',
          get(row, 'data.billing.amount', '') || '',
          get(row, 'data.billing.methodSelect') || get(row, 'data.billing.method') || '',
          get(row, 'data.billing.manifest', '') || '',
          get(getStatusIcon(row, { isAdmin: false }), 'title', '') || '',
          ORDER_TYPE_LABELS[get(row, 'data.order.orderType')] || get(row, 'data.order.orderType', '') || '',
          getRejectionReason(row),
        ]);
      });
      const [start, end] = range;
      exportToCsv(`Orders-${start.format('YYYY-MM-DD')}-to-${end.format('YYYY-MM-DD')}.csv`, rows);
    } catch (err) {
      message.error(`Failed to export orders: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const renderDaySelect = (value, onChange) => (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      style={{ minWidth: 160 }}
      maxTagCount={value.length === WEEKDAY_LABELS.length ? 0 : 2}
      maxTagPlaceholder={value.length === WEEKDAY_LABELS.length ? 'All' : undefined}
      size="small"
      allowClear={false}
    >
      {WEEKDAY_LABELS.map(d => (
        <Select.Option key={d} value={d}>{d}</Select.Option>
      ))}
    </Select>
  );

  return (
    <div css={css(styles.container)}>
      <div css={css(styles.titleRow)}>
        <div css={css(styles.title)}>Shipping Dashboard</div>
        <Button
          onClick={refreshAll}
          icon="reload"
          loading={loading || recentLoading}
          css={css(styles.refreshBtn)}
        >
          Refresh
        </Button>
      </div>

      <div css={css(styles.header)}>
        <div css={css(styles.headerLeft)}>
          {customerLogo && <img src={customerLogo} alt="Customer Logo" css={css(styles.logoImg)} />}
          {customerDBA && <div css={css(styles.dba)}>{customerDBA}</div>}
        </div>
        <div css={css(styles.headerRight)}>
          <Input.Search
            placeholder="Search every table on this page"
            value={search}
            allowClear
            style={{ maxWidth: 280 }}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            mode="multiple"
            placeholder="Licenses"
            value={selectedLicenses}
            onChange={vals => setSelectedLicenses(vals)}
            css={css(styles.licenseSelect)}
            maxTagCount={2}
            allowClear={false}
          >
            {allLicenses.map(l => (
              <Select.Option key={l} value={l}>{l}</Select.Option>
            ))}
          </Select>
          {showOrderDefFilter && (
            <Select
              value={orderDef}
              onChange={setOrderDef}
              style={{ minWidth: 140 }}
              aria-label="Order definition filter"
            >
              <Select.Option value="all">All Orders</Select.Option>
              <Select.Option value="int">Internal Orders</Select.Option>
              <Select.Option value="ext">External Orders</Select.Option>
            </Select>
          )}
          <DatePicker.RangePicker
            value={range}
            onChange={v => v && v[0] && v[1] && setRange(v)}
            allowClear={false}
          />
          <div css={css(styles.headerActions)}>
            {showShip('ordersReport') && (
              <Button onClick={exportOrders} icon="download" loading={exporting} css={css(styles.exportBtn)}>Export Orders</Button>
            )}
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        {showShip('kpis') && (
        <Row type="flex" gutter={12} css={css(styles.kpiRow)}>
          {KPI_DEFS.map(def => (
            <Col key={def.key} xs={12} sm={8} md={4}>
              <Card css={css(styles.kpiCard)}>
                <div css={css(styles.kpiLabel)}>{def.title}</div>
                <div css={css(styles.kpiValue)}>{counts[def.key] || 0}</div>
              </Card>
            </Col>
          ))}
        </Row>
        )}

        <Row gutter={12} css={css(styles.section)}>
          {showShip('deliveriesByDay') && (
          <Col xs={24} md={12}>
            <Card
              title="Deliveries by Day of the Week"
              extra={renderDaySelect(lineDays, setLineDays)}
              css={css(styles.section)}
            >
              <div css={css(styles.chartScroll)}>
                <div
                  css={css(styles.chartInner)}
                  style={{ width: Math.max(lineSeriesFiltered.length, CHART_VISIBLE_DAYS) * CHART_PX_PER_DAY }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineSeriesFiltered} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={fmtTick} interval={0} />
                      <YAxis allowDecimals={false} />
                      <ChartTooltip labelFormatter={fmtTick} />
                      <Legend />
                      <Line type="monotone" dataKey="delivered" stroke="#52c41a" name="Delivered" />
                      <Line type="monotone" dataKey="rejected" stroke="#f5222d" name="Full Rejection" />
                      <Line type="monotone" dataKey="partial" stroke="#faad14" name="Partial Rejection" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
            <Card
              title="Average Deliveries by Day of the Week"
              extra={renderDaySelect(avgDays, setAvgDays)}
            >
              <div css={css(styles.chartWrap)}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={avgFiltered} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" interval={0} />
                    <YAxis allowDecimals={true} />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="average" name="Avg Deliveries" fill="#52c41a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </Col>
          )}
          {showShip('topLocations') && (
          <Col xs={24} md={12}>
            <Card title={`Top ${TOP_LOCATIONS_LIMIT} Delivery Locations`} css={css(styles.licenseNoWrap)}>
              <Table
                columns={topLocationColumns}
                dataSource={searchTable(topLocationColumns, topLocations, search, { useRender: true })}
                rowKey="licenseNumber"
                pagination={false}
                size="small"
              />
            </Card>
          </Col>
          )}
        </Row>
      </Spin>

      <Spin spinning={recentLoading}>
        {showShip('recentCompleted') && (
        <Card title="Recent Completed Deliveries" css={css(styles.section)}>
          <Table
            columns={baseColumns}
            dataSource={searchTable(baseColumns, recent.completed, search, { useRender: true })}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
        )}

        {showShip('recentRejections') && (
        <Card title="Recent Rejections" css={css(styles.section)}>
          <Table
            columns={rejectionColumns}
            dataSource={searchTable(rejectionColumns, recent.rejected, search, { useRender: true })}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
        )}
      </Spin>

      <Spin spinning={returnsLoading}>
        {showShip('reconReturns') && (
        <Card title="Reconciliation Returns Scheduled" css={css(styles.section)}>
          <Table
            columns={reconReturnColumns}
            dataSource={searchTable(reconReturnColumns, returns.cashReturns, search, { useRender: true })}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
        )}

        {showShip('rejectionReturns') && (
        <Card title="Rejection Returns Scheduled" css={css(styles.section)}>
          <Table
            columns={rejectionReturnColumns}
            dataSource={searchTable(rejectionReturnColumns, returns.rejectionReturns, search, { useRender: true })}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
        )}
      </Spin>

      <OrderModal editModal={editModal} onClose={closeOrder} />
    </div>
  );
};
